import {
  FiveElement,
  Hexagram,
  Trigram,
  generates,
  hexagramLines,
  overcomes,
  trigramElement,
  trigramNameZh,
  YaoLine
} from './meihua';

/* =====================================================================
 * 体用 · 生克 —— 卦象标注与关系判定
 *
 * 传统梅花易数规则：动爻所在之卦为用卦，不动之卦为体卦（体卦不动，用卦变）。
 * 本模块只做「读盘」层面的推导与文案，不改动 core/meihua.ts 里已冻结的排盘函数。
 * ===================================================================== */

export type TrigramPosition = 'upper' | 'lower';

export const positionLabel = (p: TrigramPosition): string => (p === 'upper' ? '上卦' : '下卦');

export const trigramAt = (h: Hexagram, p: TrigramPosition): Trigram => (p === 'upper' ? h.upper : h.lower);

/** 六爻自下而上的传统爻名：初九 / 六二 / 九三 / 六四 / 九五 / 上六 … */
export function yaoName(pos: number, lines: YaoLine[]): string {
  if (pos < 1 || pos > 6) return '';
  const yang = lines[pos - 1] === 'yang';
  if (pos === 1) return yang ? '初九' : '初六';
  if (pos === 6) return yang ? '上九' : '上六';
  const ord = ['二', '三', '四', '五'][pos - 2];
  return yang ? `九${ord}` : `六${ord}`;
}

export interface TiYong {
  /** 体卦（不动之卦）：自身、主方、内在条件 */
  body: Trigram;
  /** 用卦（动爻所在之卦）：对方、所测之事、外部环境 */
  use: Trigram;
  bodyPos: TrigramPosition;
  usePos: TrigramPosition;
  movingLine: number;
  movingIn: TrigramPosition;
  yaoName: string;
}

/** 本卦的体用定位。无动爻（不应出现）时返回 null。 */
export function assignTiYong(h: Hexagram): TiYong | null {
  const ml = h.movingLine ?? 0;
  if (ml < 1 || ml > 6) return null;
  const movingIn: TrigramPosition = ml >= 4 ? 'upper' : 'lower';
  const bodyPos: TrigramPosition = movingIn === 'upper' ? 'lower' : 'upper';
  return {
    body: trigramAt(h, bodyPos),
    use: trigramAt(h, movingIn),
    bodyPos,
    usePos: movingIn,
    movingLine: ml,
    movingIn,
    yaoName: yaoName(ml, hexagramLines(h))
  };
}

/* ---------------------------- 五行外观 ---------------------------- */

export interface ElementTheme {
  fg: string;
  bg: string;
  ring: string;
}

export const ELEMENT_THEME: Record<FiveElement, ElementTheme> = {
  [FiveElement.wood]: { fg: '#5fb98a', bg: 'rgba(76, 175, 109, .14)', ring: 'rgba(76, 175, 109, .34)' },
  [FiveElement.fire]: { fg: '#ff8a75', bg: 'rgba(224, 82, 68, .14)', ring: 'rgba(224, 82, 68, .32)' },
  [FiveElement.earth]: { fg: '#d8ac72', bg: 'rgba(201, 154, 91, .14)', ring: 'rgba(201, 154, 91, .32)' },
  [FiveElement.metal]: { fg: '#a8c0d0', bg: 'rgba(143, 168, 184, .14)', ring: 'rgba(143, 168, 184, .34)' },
  [FiveElement.water]: { fg: '#7ab8f5', bg: 'rgba(74, 144, 217, .14)', ring: 'rgba(74, 144, 217, .32)' }
};

export const elementTheme = (e: FiveElement): ElementTheme => ELEMENT_THEME[e];

/** 乾金 / 坤土 / 坎水 … 单字卦名带五行 */
export const elementTag = (t: Trigram): string => `${trigramNameZh(t)}${trigramElement(t)}`;

export const SHENG_CYCLE: FiveElement[] = [
  FiveElement.wood, FiveElement.fire, FiveElement.earth, FiveElement.metal, FiveElement.water
];
/** 木克土、土克水、水克火、火克金、金克木 */
export const KE_CYCLE: FiveElement[] = [
  FiveElement.wood, FiveElement.earth, FiveElement.water, FiveElement.fire, FiveElement.metal
];

/* ---------------------------- 体用关系 ---------------------------- */

export type PairKey = '用生体' | '体生用' | '体克用' | '用克体' | '比和';
export type ToneKey = 'ji' | 'shun' | 'ping' | 'hao' | 'ni';

export interface PairInfo {
  key: PairKey;
  /** 施动方（生克的主语） */
  actor: '体' | '用';
  /** 受动方 */
  target: '体' | '用';
  /** 生 / 克 / 比 */
  kind: '生' | '克' | '比';
  score: number;
  tone: string;
  toneKey: ToneKey;
  color: string;
  bg: string;
  head: string;
  detail: string;
  advice: string;
}

/* 吉凶用传统口诀用字：用生体大吉、比和吉、体克用小吉、体生用小凶、用克体大凶 */
const PAIR_SPEC: Record<PairKey, Omit<PairInfo, 'key' | 'target'>> = {
  用生体: {
    kind: '生', actor: '用', score: 2, tone: '大吉', toneKey: 'ji',
    color: '#5fb98a', bg: 'rgba(76, 175, 109, .14)',
    head: '他来生我 · 得助之象',
    detail: '用卦（对方、所测之事、外部环境）的五行生物体卦，气由外而内灌入。',
    advice: '倾向于借力推进：机会、资源、人情多在自己一边，此时进比守划算。'
  },
  体克用: {
    kind: '克', actor: '体', score: 1, tone: '小吉', toneKey: 'shun',
    color: '#e0a04a', bg: 'rgba(224, 160, 74, .14)',
    head: '我能制事 · 可得但费力',
    detail: '体卦克制用卦，事在自己掌控范围内，古谓「我克者为财」。',
    advice: '倾向于主动出手，但要自己出力气：先规划、再落实，坐等则事不成。'
  },
  比和: {
    kind: '比', actor: '体', score: 0, tone: '吉', toneKey: 'ping',
    color: '#c9a227', bg: 'rgba(201, 162, 39, .13)',
    head: '同气比和 · 得助无碍',
    detail: '体用五行相同，彼此无生无克，同气相求，古法作吉。',
    advice: '倾向于合作同行：平稳有进展，不宜急攻，也不宜独断。'
  },
  体生用: {
    kind: '生', actor: '体', score: -1, tone: '小凶', toneKey: 'hao',
    color: '#9a9488', bg: 'rgba(242, 238, 227, .07)',
    head: '体气外泄 · 付出之象',
    detail: '体卦生用卦，我生彼而气外泄，多主投入、迁就、为人作嫁。',
    advice: '倾向于节制投入：先算清成本与回报，别把资源泄在不当处。'
  },
  用克体: {
    kind: '克', actor: '用', score: -2, tone: '大凶', toneKey: 'ni',
    color: '#ff8a75', bg: 'rgba(224, 82, 68, .14)',
    head: '彼来克我 · 压力在身',
    detail: '用卦克制体卦，外来压力加于自身，事难由自己主导。',
    advice: '倾向于守而不攻：延缓、回避正面冲突、先补自身短板再谋进展。'
  }
};

const TARGET_FOR_ACTOR: Record<'体' | '用', '体' | '用'> = { 体: '用', 用: '体' };

export function pairKey(body: Trigram, use: Trigram): PairKey {
  const b = trigramElement(body);
  const u = trigramElement(use);
  if (b === u) return '比和';
  if (generates(u) === b) return '用生体';
  if (overcomes(u) === b) return '用克体';
  if (generates(b) === u) return '体生用';
  return '体克用';
}

export function pairInfo(body: Trigram, use: Trigram): PairInfo {
  const key = pairKey(body, use);
  const spec = PAIR_SPEC[key];
  return { key, target: TARGET_FOR_ACTOR[spec.actor], ...spec };
}

/* ---------------------- 互卦 / 变卦对体卦的作用 ---------------------- */

export type RefKey = '生体' | '克体' | '比和' | '体生' | '体克';

/** 施动方：body＝体卦主动，other＝该卦主动作用于体卦 */
export type ActorSide = 'body' | 'other';

export interface RefInfo {
  key: RefKey;
  /** 生 / 克 / 比 —— 关系性质 */
  kind: '生' | '克' | '比';
  actor: ActorSide;
  target: ActorSide;
  score: number;
  color: string;
  bg: string;
  desc: string;
}

const REF_SPEC: Record<PairKey, RefInfo> = {
  用生体: { key: '生体', kind: '生', actor: 'other', target: 'body', score: 1, color: '#5fb98a', bg: 'rgba(76, 175, 109, .14)', desc: '生扶体卦：得外援，事有助力' },
  体克用: { key: '体克', kind: '克', actor: 'body', target: 'other', score: 0.5, color: '#e0a04a', bg: 'rgba(224, 160, 74, .14)', desc: '受体卦所制：我方能掌控，但要出力' },
  比和: { key: '比和', kind: '比', actor: 'body', target: 'body', score: 0.5, color: '#c9a227', bg: 'rgba(201, 162, 39, .13)', desc: '与体卦同气：平稳相辅，无大起伏' },
  体生用: { key: '体生', kind: '生', actor: 'body', target: 'other', score: -0.5, color: '#9a9488', bg: 'rgba(242, 238, 227, .07)', desc: '泄体卦之气：精力、钱财往外流' },
  用克体: { key: '克体', kind: '克', actor: 'other', target: 'body', score: -1, color: '#ff8a75', bg: 'rgba(224, 82, 68, .14)', desc: '克制体卦：外来之阻，压力加身' }
};

export function refInfo(body: Trigram, other: Trigram): RefInfo {
  return REF_SPEC[pairKey(body, other)];
}

export interface TrigramRef {
  pos: TrigramPosition;
  trigram: Trigram;
  tag: string;
  ref: RefInfo;
}

/* ---------------------------- 月令旺衰 ---------------------------- */

export interface LingInfo {
  label: string;
  desc: string;
  color: string;
  bg: string;
  score: number;
}

/** 从 "子 (水)" 这类月令字符串里取五行 */
export function elementFromYueling(text?: string | null): FiveElement | null {
  if (!text) return null;
  for (const e of Object.values(FiveElement) as FiveElement[]) {
    if (text.indexOf(e) >= 0) return e;
  }
  return null;
}

export function lingInfoOnBody(body: Trigram, yueling?: string | null): LingInfo | null {
  const m = elementFromYueling(yueling);
  if (!m) return null;
  const b = trigramElement(body);
  if (m === b) {
    return { label: '体得令', score: 1, color: '#5fb98a', bg: 'rgba(76, 175, 109, .14)', desc: `月令当令之气与体卦同五行（${m}），体卦气势最足` };
  }
  if (generates(m) === b) {
    return { label: '体得生扶', score: 0.5, color: '#c9a227', bg: 'rgba(201, 162, 39, .13)', desc: `月令${m}生体卦${b}：时令在帮我，做事借力不难` };
  }
  if (overcomes(m) === b) {
    return { label: '体受令克', score: -1, color: '#ff8a75', bg: 'rgba(224, 82, 68, .14)', desc: `月令${m}克体卦${b}：时令压身，自身气势偏弱` };
  }
  if (generates(b) === m) {
    return { label: '体气泄令', score: -0.5, color: '#9a9488', bg: 'rgba(242, 238, 227, .07)', desc: `体卦${b}生月令${m}：气势外散，多劳而少收` };
  }
  return { label: '体劳克令', score: 0, color: '#e0a04a', bg: 'rgba(224, 160, 74, .14)', desc: `体卦${b}克月令${m}：能制其事，但须自己出力` };
}

/* ---------------------------- 整体判断 ---------------------------- */

export interface TiYongReading {
  original: Hexagram;
  mutual: Hexagram;
  changed: Hexagram;
  tiYong: TiYong;
  pair: PairInfo;
  /** 变卦中由用卦所变之卦（体卦不动），主事之终 */
  changedRef: TrigramRef;
  /** 互卦上下两卦对体卦的作用，主事之中 */
  mutualRefs: TrigramRef[];
  yueling: string | null;
  ling: LingInfo | null;
  total: number;
  tone: string;
  toneKey: ToneKey;
  summary: string;
}

export interface ToneBand {
  tone: string;
  toneKey: ToneKey;
  color: string;
}

const TONE_BANDS: (ToneBand & { min: number })[] = [
  { min: 4.5, tone: '大吉 · 可进', toneKey: 'ji', color: '#5fb98a' },
  { min: 2, tone: '小吉 · 宜主动', toneKey: 'shun', color: '#7ab85f' },
  { min: -2, tone: '中平 · 量力而行', toneKey: 'ping', color: '#c9a227' },
  { min: -4.5, tone: '小凶 · 宜守', toneKey: 'hao', color: '#e0a04a' },
  { min: -99, tone: '大凶 · 先解压力', toneKey: 'ni', color: '#ff8a75' }
];

/** 分值 → 断语档位（各卦皆用同一把尺） */
export function toneForScore(score: number): ToneBand {
  const b = TONE_BANDS.find((x) => score >= x.min) ?? TONE_BANDS[TONE_BANDS.length - 1];
  return { tone: b.tone, toneKey: b.toneKey, color: b.color };
}

const mean = (xs: number[]): number => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

export function buildReading(
  original: Hexagram,
  mutual: Hexagram,
  changed: Hexagram,
  yueling?: string | null
): TiYongReading | null {
  const tiYong = assignTiYong(original);
  if (!tiYong) return null;
  const pair = pairInfo(tiYong.body, tiYong.use);

  // 变卦只换「用」位：体卦不动，故变卦仍取本卦的 usePos 作对照位。
  // 不可改成 bodyPos —— 那会把体/用倒置，断卦结论随之相反。
  const changedPos = tiYong.usePos;
  const changedTri = trigramAt(changed, changedPos);
  const changedRef: TrigramRef = {
    pos: changedPos,
    trigram: changedTri,
    tag: elementTag(changedTri),
    ref: refInfo(tiYong.body, changedTri)
  };

  const mutualRefs: TrigramRef[] = (['upper', 'lower'] as TrigramPosition[]).map((pos) => {
    const t = trigramAt(mutual, pos);
    return { pos, trigram: t, tag: elementTag(t), ref: refInfo(tiYong.body, t) };
  });

  const ling = lingInfoOnBody(tiYong.body, yueling ?? null);
  const total =
    pair.score * 2 + changedRef.ref.score * 1.5 + mean(mutualRefs.map((r) => r.ref.score)) + (ling ? ling.score : 0);

  const band = TONE_BANDS.find((b) => total >= b.min) ?? TONE_BANDS[TONE_BANDS.length - 1];

  const summary = [
    `体卦${elementTag(tiYong.body)}（${positionLabel(tiYong.bodyPos)}）不动，用卦${elementTag(tiYong.use)}（${positionLabel(tiYong.usePos)}）值${tiYong.yaoName}而动，${pair.key}`,
    `互卦${mutualRefs.map((r) => `${r.tag}·${r.ref.key}`).join('，')}，为事之中`,
    `变卦${changedRef.tag}·${changedRef.ref.key}，为事之终`
  ].join('；') + (ling ? `；月令${yueling}，${ling.label}` : '。');

  return {
    original, mutual, changed, tiYong, pair, changedRef, mutualRefs,
    yueling: yueling ?? null, ling, total, tone: band.tone, toneKey: band.toneKey, summary
  };
}

/** 传统动爻记号：阳动 ○（老阳），阴动 ×（老阴） */
export const movingYaoMark = (line: YaoLine | undefined): string => (line === 'yang' ? '○' : '×');
/* =====================================================================
 * 焦点视图 —— 生克判读面板按「本卦 / 互卦 / 变卦」切换所看的内容
 * 体卦自始至终不动（由本卦定），变的只是与体卦对照的那一卦。
 * ===================================================================== */

export type FocusKey = 'original' | 'mutual' | 'changed';

export interface FocusMeta {
  label: string;
  short: string;
  meaning: string;
  hint: string;
}

export const FOCUS_META: Record<FocusKey, FocusMeta> = {
  original: {
    label: '本卦',
    short: '现况',
    meaning: '现况 · 体用在此定',
    hint: '本卦看当下：不动之卦为体，动爻所在之卦为用，体用生克是吉凶主轴。'
  },
  mutual: {
    label: '互卦',
    short: '过程',
    meaning: '过程 · 卦中互见',
    hint: '互卦看过程：由本卦中间四爻互出，看它对体卦是生是克，即中途顺逆。'
  },
  changed: {
    label: '变卦',
    short: '结果',
    meaning: '结果 · 用卦所变',
    hint: '变卦看结果：体卦不动，用卦变出之卦与体卦的关系，定终局倾向。'
  }
};

export const FOCUS_ORDER: FocusKey[] = ['original', 'mutual', 'changed'];

/** 互卦 / 变卦 对体卦各关系的吉凶用字（与体用口诀同一套用字） */
export const REF_GRADE: Record<RefKey, string> = {
  生体: '吉',
  克体: '凶',
  比和: '吉',
  体生: '小凶',
  体克: '小吉'
};

export interface FocusItem {
  /** 用 / 互上 / 互下 / 变 */
  role: string;
  pos: TrigramPosition;
  trigram: Trigram;
  tag: string;
  elem: FiveElement;
  kind: '生' | '克' | '比';
  key: string;
  color: string;
  bg: string;
  desc: string;
  score: number;
  actor: ActorSide;
  target: ActorSide;
  /** 吉凶用字：大吉 / 小吉 / 吉 / 小凶 / 凶 */
  grade: string;
}

export interface FocusView {
  focus: FocusKey;
  meta: FocusMeta;
  bodyTag: string;
  bodyElem: FiveElement;
  items: FocusItem[];
  /** 该卦对体卦的作用分（与总盘同一把尺，已放大到 -5..+5 档位） */
  score: number;
  tone: string;
  toneColor: string;
  detail: string;
  advice: string;
}

const REF_ADVICE: Record<RefKey, string> = {
  生体: '此卦之气来生体卦，是助力：顺着这股势推进即可。',
  克体: '此卦之气来克体卦，是阻力：宜缓、宜守，先避开正面压力。',
  比和: '与体卦同气，平稳相辅：不宜急攻，也不宜独断。',
  体生: '体卦之气泄于此卦：多主付出、投入，注意成本。',
  体克: '此卦受体卦所制：事在掌控中，但要自己出力。'
};

const focusAdviceFor = (items: FocusItem[]): string => {
  const ks = items.map((i) => i.key);
  if (ks.indexOf('克体') >= 0) return '过程或结果中有克体之象：留意人事与环境变化，宁可留一手，别把话说满。';
  if (ks.indexOf('生体') >= 0) return '过程或结果中有生体之象：外力可用，顺势推进比硬扛划算。';
  if (ks.indexOf('比和') >= 0) return '气与体卦比和：平稳推进，合作的成分重于单打独斗。';
  if (ks.indexOf('体生') >= 0) return '体气外泄之象：算清投入与回报，别做过度的前期垫付。';
  return '体卦能制此卦：主动出手能得，但仍要自己花力气落实。';
};

export function buildFocusView(reading: TiYongReading, focus: FocusKey): FocusView {
  const { tiYong, pair, changedRef, mutualRefs } = reading;
  const bodyElem = trigramElement(tiYong.body);
  const meta = FOCUS_META[focus];

  const toItem = (role: string, pos: TrigramPosition, trigram: Trigram, ref: RefInfo): FocusItem => ({
    role,
    pos,
    trigram,
    tag: elementTag(trigram),
    elem: trigramElement(trigram),
    kind: ref.kind,
    key: ref.key,
    color: ref.color,
    bg: ref.bg,
    desc: ref.desc,
    score: ref.score,
    actor: ref.actor,
    target: ref.target,
    grade: REF_GRADE[ref.key]
  });

  let items: FocusItem[];
  let rawScore: number;
  let detail: string;

  if (focus === 'original') {
    const base = toItem('用', tiYong.usePos, tiYong.use, refInfo(tiYong.body, tiYong.use));
    items = [{ ...base, key: pair.key, desc: pair.detail, color: pair.color, bg: pair.bg, grade: pair.tone }];
    rawScore = pair.score;
    detail = pair.detail;
  } else if (focus === 'mutual') {
    items = mutualRefs.map((r) => toItem(r.pos === 'upper' ? '互上' : '互下', r.pos, r.trigram, r.ref));
    rawScore = mean(items.map((i) => i.score));
    detail = items.map((i) => `${i.role}${i.tag}：${i.desc}`).join('；');
  } else {
    items = [toItem('变', changedRef.pos, changedRef.trigram, changedRef.ref)];
    rawScore = changedRef.ref.score;
    detail = `变卦${changedRef.tag}（${positionLabel(changedRef.pos)}，由用卦${elementTag(tiYong.use)}变来）：${changedRef.ref.desc}`;
  }

  const score = Math.round(rawScore * 2 * 10) / 10;
  const band = toneForScore(score);

  const advice =
    focus === 'original'
      ? pair.advice
      : focus === 'mutual'
        ? `${items.map((i) => REF_ADVICE[i.key as RefKey]).join(' ')}${focusAdviceFor(items)}`
        : `${REF_ADVICE[items[0].key as RefKey]} ${focusAdviceFor(items)}`;

  return {
    focus,
    meta,
    bodyTag: elementTag(tiYong.body),
    bodyElem,
    items,
    score,
    tone: band.tone,
    toneColor: band.color,
    detail,
    advice
  };
}
